<template>
  <div class="word-table-container">
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <form class="filter-form">
        <div class="form-item">
          <label>单词书:</label>
          <div class="custom-book-select">
            <div
              class="select-trigger"
              @click="toggleDropdown"
              :class="{ active: dropdownVisible }"
            >
              <span class="select-value">{{
                selectedBookName || "请选择单词书"
              }}</span>
              <i class="select-arrow" :class="{ 'arrow-up': dropdownVisible }"
                >▼</i
              >
            </div>

            <div v-if="dropdownVisible" class="select-dropdown">
              <div class="dropdown-container">
                <!-- 左侧分类 -->
                <div class="book-categories">
                  <div
                    v-for="group in bookList"
                    :key="group.bookType"
                    class="category-item"
                    :class="{ active: selectedCategory === group.bookType }"
                    @click="selectCategory(group.bookType)"
                  >
                    {{ group.bookTypeInfo }}
                  </div>
                </div>

                <!-- 右侧单词书选项 -->
                <div class="book-options">
                  <div
                    v-for="book in currentBooks"
                    :key="book.bookId"
                    class="book-option"
                    :class="{ selected: filterForm.bookId === book.bookId }"
                    @click="selectBook(book)"
                  >
                    {{ book.bookName }}
                  </div>

                  <div v-if="currentBooks.length === 0" class="no-books">
                    请先选择左侧分类
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-item">
          <label>单词搜索:</label>
          <div class="search-input-group">
            <input
              type="text"
              v-model="filterForm.content"
              class="form-input search-input"
              placeholder="请输入要搜索的单词"
              @keyup.enter="loadWords"
            />
            <button
              v-if="filterForm.content"
              type="button"
              class="btn btn-clear"
              @click="clearSearch"
              title="清空搜索"
            >
              ✕
            </button>
          </div>
        </div>

        <div class="form-item">
          <label>每页数量:</label>
          <select v-model.number="filterForm.pageSize" class="form-select">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>

        <div class="form-item">
          <button
            type="button"
            class="btn btn-primary"
            @click="loadWords"
            :disabled="loading"
          >
            {{ loading ? "查询中..." : "查询" }}
          </button>
        </div>
      </form>

      <!-- 分页控制 -->
      <div class="pagination-controls">
        <button
          type="button"
          class="btn btn-pagination"
          :disabled="currentPage <= 1"
          @click="previousPage"
        >
          上一页
        </button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button
          type="button"
          class="btn btn-pagination"
          :disabled="currentPage >= totalPages"
          @click="nextPage"
        >
          下一页
        </button>
      </div>
    </div>

    <!-- 左右分栏布局 -->
    <div class="word-content">
      <!-- 左侧：单词列表 -->
      <div class="word-list-panel">
        <div class="panel-header">
          <span>单词列表</span>
          <span class="word-count">共 {{ total }} 个单词</span>
        </div>
        <div class="panel-body">
          <div class="word-list" :class="{ loading: loading }">
            <div
              v-for="word in wordList"
              :key="word.id"
              class="word-item"
              :class="{ active: selectedWordId === word.id }"
              @click="selectWord(word)"
            >
              <div class="word-content-text">
                <div class="word-header">
                  <span class="word-text">{{ word.content }}</span>
                  <span class="word-id">ID: {{ word.wordId || word.id }}</span>
                </div>
                <span v-if="word.pronunciation" class="word-pronunciation">
                <span v-if="word.pronunciation.uk"
                    >英 [{{ word.pronunciation.uk }}]</span
                  >
                  <span v-if="word.pronunciation.us"
                    >美 [{{ word.pronunciation.us }}]</span
                  >
                  <span v-if="word.pronunciation.ko"
                    >韩 [{{ word.pronunciation.ko }}]</span
                  >
                  <span v-if="word.pronunciation.jp"
                    >日 [{{ word.pronunciation.jp }}]</span
                  >
                  <span v-if="word.pronunciation.romaji"
                    >罗 [{{ word.pronunciation.romaji }}]</span
                  >
                </span>
              </div>
              <div class="word-definitions-preview">
                <span
                  v-for="(def, index) in (word.definitions || []).slice(0, 1)"
                  :key="index"
                  class="definition-tag"
                >
                  {{ def.pos }} {{ def.meaningCn }}
                </span>
                <span
                  v-if="word.definitions && word.definitions.length > 1"
                  class="more-tag"
                  >+{{ word.definitions.length - 1 }}</span
                >
              </div>
              <div class="word-meta">
                <span v-if="word.difficulty" class="difficulty-badge">{{
                  getDifficultyText(word.difficulty)
                }}</span>
                <span v-if="word.tags && word.tags.length > 0" class="tags">
                  <span
                    v-for="tag in word.tags.slice(0, 2)"
                    :key="tag"
                    class="tag-item"
                    >{{ tag }}</span
                  >
                  <span v-if="word.tags.length > 2" class="tag-more"
                    >+{{ word.tags.length - 2 }}</span
                  >
                </span>
              </div>
            </div>

            <!-- 加载状态 -->
            <div v-if="loading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>加载中...</span>
            </div>

            <!-- 空状态 -->
            <div v-if="!loading && wordList.length === 0" class="empty-content">
              <span>暂无单词数据</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：单词详情 -->
      <div class="word-detail-panel">
        <div class="panel-header">
          <span>单词详情</span>
        </div>
        <div class="panel-body">
          <div v-if="selectedWord" class="word-detail-content">
            <!-- 单词标题 -->
            <div class="word-title-section">
              <div class="word-title">
                <span class="word-text-large">{{ selectedWord.content }}</span>
                <span
                  v-if="selectedWord.pronunciation"
                  class="word-pronunciation-large"
                >
                  <span v-if="selectedWord.pronunciation.uk"
                    >英 [{{ selectedWord.pronunciation.uk }}]</span
                  >
                  <span v-if="selectedWord.pronunciation.us"
                    >美 [{{ selectedWord.pronunciation.us }}]</span
                  >
                  <span v-if="selectedWord.pronunciation.ko"
                    >韩 [{{ selectedWord.pronunciation.ko }}]</span
                  >
                  <span v-if="selectedWord.pronunciation.jp"
                    >日 [{{ selectedWord.pronunciation.jp }}]</span
                  >
                  <span v-if="selectedWord.pronunciation.romaji"
                    >罗 [{{ selectedWord.pronunciation.romaji }}]</span
                  >
                </span>
                <button
                  class="btn-edit-word"
                  @click="openEditDialog"
                  title="编辑单词"
                >
                  编辑
                </button>
              </div>
              <div class="word-meta-large">
                <span
                  v-if="selectedWord.difficulty"
                  class="difficulty-badge-large"
                  >{{ getDifficultyText(selectedWord.difficulty) }}</span
                >
                <span
                  v-if="selectedWord.tags && selectedWord.tags.length > 0"
                  class="tags-large"
                >
                  <span
                    v-for="tag in selectedWord.tags"
                    :key="tag"
                    class="tag-badge-large"
                    >{{ tag }}</span
                  >
                </span>
              </div>
            </div>

            <!-- 完整定义 -->
            <div class="detail-section">
              <h4>定义</h4>
              <div class="definitions-list">
                <div
                  v-for="(def, index) in selectedWord.definitions || []"
                  :key="index"
                  class="definition-item"
                >
                  <span class="pos">{{ def.pos }}</span>
                  <span class="meaning">{{ def.meaningCn }}</span>
                </div>
                <div
                  v-if="
                    !selectedWord.definitions ||
                    selectedWord.definitions.length === 0
                  "
                  class="empty-hint"
                >
                  暂无定义
                </div>
              </div>
            </div>

            <!-- 例句 -->
            <div
              v-if="selectedWord.examples && selectedWord.examples.length > 0"
              class="detail-section"
            >
              <h4>例句</h4>
              <div class="examples-list">
                <div
                  v-for="(example, index) in selectedWord.examples"
                  :key="index"
                  class="example-item"
                >
                  <div
                    class="example-sentence"
                    v-html="formatExampleSentence(example)"
                  ></div>
                  <div class="example-translation">
                    {{ example.translation }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 特征 -->
            <div
              v-if="selectedWord.features && selectedWord.features.length > 0"
              class="detail-section"
            >
              <h4>特征</h4>
              <div class="features-list">
                <div
                  v-for="(feature, index) in selectedWord.features"
                  :key="index"
                  class="feature-item"
                >
                  <div class="feature-type">{{ feature.type }}</div>
                  <div class="feature-items">
                    <div
                      v-for="(item, itemIndex) in feature.items"
                      :key="itemIndex"
                      class="feature-item-content"
                    >
                      <span class="feature-content">{{ item.content }}</span>
                      <span v-if="item.meaningCn" class="feature-meaning">{{
                        item.meaningCn
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 相似单词 -->
            <div
              v-if="selectedWord.similarWords && selectedWord.similarWords.length > 0"
              class="detail-section"
            >
              <h4>相似单词</h4>
              <div class="similar-words-list">
                <div
                  v-for="(similarWord, index) in selectedWord.similarWords"
                  :key="index"
                  class="similar-word-item"
                >
                  <div class="similar-word-header">
                    <span class="similar-word-content">{{ similarWord.content }}</span>
                    <span class="similar-word-id">ID: {{ similarWord.wordId }}</span>
                  </div>
                  <div class="similar-word-definitions">
                    <div
                      v-for="(def, defIndex) in similarWord.definitions || []"
                      :key="defIndex"
                      class="similar-definition"
                    >
                      <span class="similar-pos">{{ def.pos }}</span>
                      <span class="similar-meaning">{{ def.meaningCn }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 未选择状态 -->
          <div v-else class="empty-detail">
            <div class="empty-icon">📖</div>
            <p>请从左侧选择一个单词查看详情</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑单词对话框 -->
    <div v-if="showEditDialog" class="dialog-overlay" @click="closeEditDialog">
      <div class="dialog-content edit-word-dialog" @click.stop>
        <div class="dialog-header">
          <h3>编辑单词</h3>
          <button class="close-btn" @click="closeEditDialog">×</button>
        </div>
        <div class="dialog-body">
          <form @submit.prevent="handleUpdateWord">
            <!-- 单词内容 -->
            <div class="form-group">
              <label>单词内容 <span class="required">*</span></label>
              <input
                type="text"
                v-model="editForm.content"
                placeholder="请输入单词"
                required
              />
            </div>

            <!-- 发音 -->
            <div class="form-group">
              <label>发音</label>
              <div class="pronunciation-group">
                <div class="pronunciation-item">
                  <label>英式发音</label>
                  <input
                    type="text"
                    v-model="editForm.pronunciation.uk"
                    placeholder="如：ˈhæpi"
                  />
                </div>
                <div class="pronunciation-item">
                  <label>美式发音</label>
                  <input
                    type="text"
                    v-model="editForm.pronunciation.us"
                    placeholder="如：ˈhæpi"
                  />
                </div>
                <div class="pronunciation-item">
                  <label>韩语音标</label>
                  <input
                    type="text"
                    v-model="editForm.pronunciation.ko"
                    placeholder="如：gakkaptta"
                  />
                </div>
                <div class="pronunciation-item">
                  <label>日语音标</label>
                  <input
                    type="text"
                    v-model="editForm.pronunciation.jp"
                    placeholder="请输入日语发音"
                  />
                </div>
                <div class="pronunciation-item">
                  <label>罗马音</label>
                  <input
                    type="text"
                    v-model="editForm.pronunciation.romaji"
                    placeholder="请输入 romaji"
                  />
                </div>
              </div>
            </div>

            <!-- 难度 -->
            <div class="form-group">
              <label>难度</label>
              <select v-model="editForm.difficulty">
                <option value="">请选择</option>
                <option value="easy">简单</option>
                <option value="medium">中等</option>
                <option value="hard">困难</option>
              </select>
            </div>

            <!-- 标签 -->
            <div class="form-group">
              <label>标签</label>
              <div class="tags-input-group">
                <div
                  v-for="(tag, index) in editForm.tags"
                  :key="index"
                  class="tag-item"
                >
                  <input
                    type="text"
                    v-model="editForm.tags[index]"
                    placeholder="标签"
                  />
                  <button
                    type="button"
                    class="btn-remove-tag"
                    @click="removeTag(index)"
                  >
                    ×
                  </button>
                </div>
                <button type="button" class="btn-add-tag" @click="addTag">
                  + 添加标签
                </button>
              </div>
            </div>

            <!-- 定义 -->
            <div class="form-group">
              <label>定义</label>
              <div class="definitions-edit-group">
                <div
                  v-for="(def, index) in editForm.definitions"
                  :key="index"
                  class="definition-edit-item"
                >
                  <select v-model="def.pos" class="pos-input">
                    <option value="">请选择</option>
                    <option value="n.">n.</option>
                    <option value="adj.">adj.</option>
                    <option value="adv.">adv.</option>
                    <option value="pron.">pron.</option>
                    <option value="phr.">phr.</option>
                    <option value="v.">v.</option>
                    <option value="vt.">vt.</option>
                    <option value="vi.">vi.</option>
                    <option value="aux.">aux.</option>
                    <option value="prep.">prep.</option>
                  </select>
                  <input
                    type="text"
                    v-model="def.meaningCn"
                    placeholder="中文释义"
                    class="meaning-input"
                  />
                  <button
                    type="button"
                    class="btn-remove"
                    @click="removeDefinition(index)"
                  >
                    ×
                  </button>
                </div>
                <button type="button" class="btn-add" @click="addDefinition">
                  + 添加定义
                </button>
              </div>
            </div>

            <!-- 例句 -->
            <div class="form-group">
              <label>例句</label>
              <div class="examples-edit-group">
                <div
                  v-for="(example, index) in editForm.examples"
                  :key="index"
                  class="example-edit-item"
                >
                  <div class="example-edit-header">
                    <input
                      type="text"
                      v-model="example.sentence"
                      placeholder="例句"
                      class="sentence-input"
                    />
                    <button
                      type="button"
                      class="btn-remove"
                      @click="removeExample(index)"
                    >
                      ×
                    </button>
                  </div>
                  <input
                    type="text"
                    v-model="example.translation"
                    placeholder="翻译"
                    class="translation-input"
                  />
                  <!-- 单词位置 -->
                  <div class="word-positions-group">
                    <div
                      v-for="(pos, posIndex) in example.wordPositions"
                      :key="posIndex"
                      class="position-item"
                    >
                      <input
                        type="number"
                        v-model.number="pos.start"
                        placeholder="开始位置"
                        min="0"
                        class="position-input"
                      />
                      <span>-</span>
                      <input
                        type="number"
                        v-model.number="pos.end"
                        placeholder="结束位置"
                        min="0"
                        class="position-input"
                      />
                      <button
                        type="button"
                        class="btn-remove-small"
                        @click="removeWordPosition(index, posIndex)"
                      >
                        ×
                      </button>
                    </div>
                    <button
                      type="button"
                      class="btn-add-small"
                      @click="addWordPosition(index)"
                    >
                      + 添加位置
                    </button>
                  </div>
                </div>
                <button type="button" class="btn-add" @click="addExample">
                  + 添加例句
                </button>
              </div>
            </div>

            <!-- 特征 -->
            <div class="form-group">
              <label>特征</label>
              <div class="features-edit-group">
                <div
                  v-for="(feature, index) in editForm.features"
                  :key="index"
                  class="feature-edit-item"
                >
                  <div class="feature-edit-header">
                    <input
                      type="text"
                      v-model="feature.type"
                      placeholder="特征类型"
                      class="feature-type-input"
                    />
                    <button
                      type="button"
                      class="btn-remove"
                      @click="removeFeature(index)"
                    >
                      ×
                    </button>
                  </div>
                  <div class="feature-items-edit">
                    <div
                      v-for="(item, itemIndex) in feature.items"
                      :key="itemIndex"
                      class="feature-item-edit"
                    >
                      <input
                        type="text"
                        v-model="item.content"
                        placeholder="内容"
                        class="feature-content-input"
                      />
                      <input
                        type="text"
                        v-model="item.meaningCn"
                        placeholder="中文释义"
                        class="feature-meaning-input"
                      />
                      <button
                        type="button"
                        class="btn-remove-small"
                        @click="removeFeatureItem(index, itemIndex)"
                      >
                        ×
                      </button>
                    </div>
                    <button
                      type="button"
                      class="btn-add-small"
                      @click="addFeatureItem(index)"
                    >
                      + 添加项
                    </button>
                  </div>
                </div>
                <button type="button" class="btn-add" @click="addFeature">
                  + 添加特征
                </button>
              </div>
            </div>

            <!-- 相似单词 -->
            <div class="form-group">
              <label>相似单词</label>
              <div class="similar-words-edit-group">
                <div
                  v-for="(similarWord, index) in editForm.similarWords"
                  :key="index"
                  class="similar-word-edit-item"
                >
                  <div class="similar-word-edit-header">
                    <input
                      type="number"
                      v-model.number="similarWord.wordId"
                      placeholder="单词ID"
                      class="similar-word-id-input"
                      min="1"
                    />
                    <input
                      type="text"
                      v-model="similarWord.content"
                      placeholder="单词内容"
                      class="similar-word-content-input"
                    />
                    <button
                      type="button"
                      class="btn-remove"
                      @click="removeSimilarWord(index)"
                    >
                      ×
                    </button>
                  </div>
                  <div class="similar-definitions-edit">
                    <div
                      v-for="(def, defIndex) in similarWord.definitions"
                      :key="defIndex"
                      class="similar-definition-edit"
                    >
                      <select v-model="def.pos" class="similar-pos-input">
                        <option value="">请选择</option>
                        <option value="n.">n.</option>
                        <option value="adj.">adj.</option>
                        <option value="adv.">adv.</option>
                        <option value="pron.">pron.</option>
                        <option value="phr.">phr.</option>
                        <option value="v.">v.</option>
                        <option value="vt.">vt.</option>
                        <option value="vi.">vi.</option>
                        <option value="aux.">aux.</option>
                        <option value="prep.">prep.</option>
                      </select>
                      <input
                        type="text"
                        v-model="def.meaningCn"
                        placeholder="中文释义"
                        class="similar-meaning-input"
                      />
                      <button
                        type="button"
                        class="btn-remove-small"
                        @click="removeSimilarDefinition(index, defIndex)"
                      >
                        ×
                      </button>
                    </div>
                    <button
                      type="button"
                      class="btn-add-small"
                      @click="addSimilarDefinition(index)"
                    >
                      + 添加释义
                    </button>
                  </div>
                </div>
                <button type="button" class="btn-add" @click="addSimilarWord">
                  + 添加相似单词
                </button>
              </div>
            </div>
          </form>
        </div>
        <div class="dialog-footer">
          <button type="button" class="cancel-btn" @click="closeEditDialog">
            取消
          </button>
          <button
            type="button"
            class="confirm-btn"
            @click="handleUpdateWord"
            :disabled="updating"
          >
            {{ updating ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { word } from "../../../api/index";

export default {
  name: "WordDetailTable",
  data() {
    return {
      loading: false,
      bookLoading: false,
      filterForm: {
        bookId: null,
        content: "", // 单词搜索内容
        pageSize: 10,
      },
      wordList: [],
      total: 0,
      currentPage: 1,
      selectedWordId: null, // 当前选中的单词ID
      selectedWord: null, // 当前选中的单词对象
      bookList: [], // 存储单词书列表
      selectedCategory: null, // 当前选中的分类
      dropdownVisible: false, // 下拉框是否可见
      showEditDialog: false, // 编辑对话框显示状态
      updating: false, // 是否正在更新
      editForm: {
        wordId: null,
        content: "",
        pronunciation: {
          uk: "",
          us: "",
          ko: "",
          jp: "",
          romaji: "",
        },
        definitions: [],
        examples: [],
        features: [],
        tags: [],
        difficulty: "",
        similarWords: [],
      },
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.filterForm.pageSize);
    },
    currentBooks() {
      if (
        this.selectedCategory === null ||
        this.selectedCategory === undefined
      ) {
        return [];
      }

      const category = this.bookList.find(
        (item) =>
          String(item.bookType) === String(this.selectedCategory) ||
          Number(item.bookType) === Number(this.selectedCategory)
      );

      return category ? category.books : [];
    },
    selectedBookName() {
      if (!this.filterForm.bookId) return "";

      for (const group of this.bookList) {
        const book = group.books.find(
          (book) => book.bookId === this.filterForm.bookId
        );
        if (book) {
          return book.bookName;
        }
      }
      return "";
    },
  },

  created() {
    this.loadBookList();
  },

  mounted() {
    // 点击外部关闭下拉框
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    normalizeBookGroups(groups) {
      return (groups || []).map((group, index) => {
        const categoryValue =
          group.bookType ?? group.bookTypeInfo ?? group.category ?? String(index);

        return {
          ...group,
          bookType: categoryValue,
          bookTypeInfo: group.bookTypeInfo ?? group.category ?? String(categoryValue),
          books: Array.isArray(group.books) ? group.books : [],
        };
      });
    },

    async loadBookList() {
      this.bookLoading = true;
      try {
        const response = await word.showBookList({});

        if (response.code === 200) {
          this.bookList = this.normalizeBookGroups(response.data);

          // 默认选择第一个分类和第一个单词书
          if (this.bookList.length > 0 && this.bookList[0].books.length > 0) {
            this.selectedCategory = this.bookList[0].bookType;
            this.filterForm.bookId = this.bookList[0].books[0].bookId;

            // 加载单词书选择后再加载单词
            this.loadWords();
          }
        } else {
          this.$message.error("加载单词书失败");
        }
      } catch (error) {
        console.error("加载单词书失败:", error);
        this.$message.error("加载单词书失败");
      } finally {
        this.bookLoading = false;
      }
    },

    toggleDropdown() {
      this.dropdownVisible = !this.dropdownVisible;
    },

    selectCategory(categoryType) {
      this.selectedCategory = categoryType;
    },

    selectBook(book) {
      this.filterForm.bookId = book.bookId;
      this.dropdownVisible = false;
      this.onBookChange();
    },

    onBookChange() {
      // 单词书改变时重置页码、清空搜索内容并重新加载
      this.currentPage = 1;
      this.selectedWordId = null;
      this.selectedWord = null;
      this.filterForm.content = ""; // 清空搜索内容
      this.loadWords();
    },

    clearSearch() {
      this.filterForm.content = "";
      this.currentPage = 1;
      this.loadWords();
    },

    handleClickOutside(event) {
      const bookSelectElement = this.$el.querySelector(".custom-book-select");

      if (bookSelectElement && !bookSelectElement.contains(event.target)) {
        this.dropdownVisible = false;
      }
    },

    async loadWords() {
      if (!this.filterForm.bookId) {
        this.$message.warning("请先选择单词书");
        return;
      }

      this.loading = true;
      try {
        const requestData = {
          bookId: this.filterForm.bookId,
          pageSize: this.filterForm.pageSize,
          pageNum: this.currentPage,
        };

        // 如果有搜索内容，添加到请求参数中
        if (this.filterForm.content && this.filterForm.content.trim()) {
          requestData.content = this.filterForm.content.trim();
        }

        console.log("请求参数:", requestData);

        const response = await word.showWord(requestData);

        console.log("API返回数据:", response);
        if (response.code === 200) {
          const rawWordList = response.data.data || [];
          // 初始化所有 null 字段为空数组或默认值，避免报错
          this.wordList = rawWordList.map((word) => ({
            ...word,
            pronunciation: word.pronunciation || null,
            definitions: word.definitions || [],
            examples: word.examples || [],
            features: word.features || [],
            tags: word.tags || [],
            difficulty: word.difficulty || null,
            similarWords: word.similarWords || [],
          }));
          this.total = response.data.total || 0;
          console.log(`加载了 ${this.wordList.length} 个单词`);
          // 如果有单词列表，默认选中第一个
          if (this.wordList.length > 0 && !this.selectedWordId) {
            this.selectWord(this.wordList[0]);
          }
        } else {
          this.$message.error(response.msg || "加载失败");
          this.wordList = [];
          this.total = 0;
        }
      } catch (error) {
        console.error("加载单词失败:", error);
        this.$message.error("加载失败，请重试");
        this.wordList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        console.log(`切换到第${this.currentPage}页`);
        this.loadWords();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        console.log(`切换到第${this.currentPage}页`);
        this.loadWords();
      }
    },

    selectWord(word) {
      this.selectedWordId = word.id;
      // 确保选中的单词所有字段都已初始化，避免 null 值报错
      this.selectedWord = {
        ...word,
        pronunciation: word.pronunciation || null,
        definitions: word.definitions || [],
        examples: word.examples || [],
        features: word.features || [],
        tags: word.tags || [],
        difficulty: word.difficulty || null,
        similarWords: word.similarWords || [],
      };
    },

    getDifficultyText(difficulty) {
      const difficultyMap = {
        easy: "简单",
        medium: "中等",
        hard: "困难",
      };
      return difficultyMap[difficulty] || difficulty;
    },

    formatExampleSentence(example) {
      if (!example || !example.sentence) {
        return "";
      }

      // 如果没有 wordPositions，直接返回转义后的文本
      if (!example.wordPositions || example.wordPositions.length === 0) {
        return this.escapeHtml(example.sentence);
      }

      const sentence = example.sentence;
      const positions = [...example.wordPositions];

      // 验证并过滤有效的位置范围
      const validPositions = positions.filter(
        (pos) =>
          pos.start >= 0 &&
          pos.end > pos.start &&
          pos.start < sentence.length &&
          pos.end <= sentence.length
      );

      if (validPositions.length === 0) {
        return this.escapeHtml(sentence);
      }

      // 按 start 位置从大到小排序，从后往前处理，避免位置偏移
      validPositions.sort((a, b) => b.start - a.start);

      // 构建结果字符串，从后往前插入加粗标签
      let result = sentence;
      validPositions.forEach((pos) => {
        const start = pos.start;
        const end = pos.end;
        const before = result.substring(0, start);
        const boldText = result.substring(start, end);
        const after = result.substring(end);

        // 转义各部分并组合
        result =
          this.escapeHtml(before) +
          `<strong>${this.escapeHtml(boldText)}</strong>` +
          this.escapeHtml(after);
      });

      return result;
    },

    escapeHtml(text) {
      if (!text) return "";
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },

    // 编辑相关方法
    openEditDialog() {
      if (!this.selectedWord) {
        this.$message.warning("请先选择一个单词");
        return;
      }

      // 初始化编辑表单
      this.editForm = {
        wordId: this.selectedWord.wordId,
        content: this.selectedWord.content || "",
        pronunciation: {
          uk: this.selectedWord.pronunciation?.uk || "",
          us: this.selectedWord.pronunciation?.us || "",
          ko: this.selectedWord.pronunciation?.ko || "",
          jp: this.selectedWord.pronunciation?.jp || "",
          romaji: this.selectedWord.pronunciation?.romaji || "",
        },
        definitions: this.selectedWord.definitions
          ? this.selectedWord.definitions.map((def) => ({
              pos: def.pos || "",
              meaningCn: def.meaningCn || "",
            }))
          : [],
        examples: this.selectedWord.examples
          ? this.selectedWord.examples.map((example) => ({
              id: example.id || null,
              sentence: example.sentence || "",
              translation: example.translation || "",
              wordPositions: example.wordPositions
                ? example.wordPositions.map((pos) => ({
                    start: pos.start || 0,
                    end: pos.end || 0,
                  }))
                : [],
            }))
          : [],
        features: this.selectedWord.features
          ? this.selectedWord.features.map((feature) => ({
              type: feature.type || "",
              items: feature.items
                ? feature.items.map((item) => ({
                    content: item.content || "",
                    meaningCn: item.meaningCn || "",
                  }))
                : [],
            }))
          : [],
        tags: this.selectedWord.tags ? [...this.selectedWord.tags] : [],
        difficulty: this.selectedWord.difficulty || "",
        similarWords: this.selectedWord.similarWords
          ? this.selectedWord.similarWords.map((similarWord) => ({
              wordId: similarWord.wordId || null,
              content: similarWord.content || "",
              definitions: similarWord.definitions
                ? similarWord.definitions.map((def) => ({
                    pos: def.pos || "",
                    meaningCn: def.meaningCn || "",
                  }))
                : [],
            }))
          : [],
      };

      this.showEditDialog = true;
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.editForm = {
        wordId: null,
        content: "",
        pronunciation: {
          uk: "",
          us: "",
          ko: "",
          jp: "",
          romaji: "",
        },
        definitions: [],
        examples: [],
        features: [],
        tags: [],
        difficulty: "",
        similarWords: [],
      };
    },

    async handleUpdateWord() {
      if (!this.editForm.content || !this.editForm.content.trim()) {
        this.$message.error("请输入单词内容");
        return;
      }

      if (!this.editForm.wordId) {
        this.$message.error("单词ID不能为空");
        return;
      }

      this.updating = true;
      try {
        // 构建请求数据
        const requestData = {
          wordId: this.editForm.wordId,
          content: this.editForm.content.trim(),
          pronunciation: {
            uk: this.editForm.pronunciation.uk?.trim() || null,
            us: this.editForm.pronunciation.us?.trim() || null,
            ko: this.editForm.pronunciation.ko?.trim() || null,
            jp: this.editForm.pronunciation.jp?.trim() || null,
            romaji: this.editForm.pronunciation.romaji?.trim() || null,
          },
          definitions: this.editForm.definitions
            .filter((def) => def.pos || def.meaningCn)
            .map((def) => ({
              pos: def.pos?.trim() || "",
              meaningCn: def.meaningCn?.trim() || "",
            })),
          examples: this.editForm.examples
            .filter((ex) => ex.sentence || ex.translation)
            .map((ex) => ({
              id: ex.id || null,
              sentence: ex.sentence?.trim() || "",
              translation: ex.translation?.trim() || "",
              wordPositions: (ex.wordPositions || []).map((pos) => ({
                start: pos.start || 0,
                end: pos.end || 0,
              })),
            })),
          features: this.editForm.features
            .filter((f) => f.type || (f.items && f.items.length > 0))
            .map((f) => ({
              type: f.type?.trim() || "",
              items: (f.items || [])
                .filter((item) => item.content || item.meaningCn)
                .map((item) => ({
                  content: item.content?.trim() || "",
                  meaningCn: item.meaningCn?.trim() || "",
                })),
            })),
          tags: this.editForm.tags
            .map((tag) => tag?.trim())
            .filter((tag) => tag),
          difficulty: this.editForm.difficulty || null,
          similarWords: this.editForm.similarWords
            .filter((sw) => sw.wordId && sw.content)
            .map((sw) => ({
              wordId: sw.wordId,
              content: sw.content?.trim() || "",
              definitions: (sw.definitions || [])
                .filter((def) => def.pos || def.meaningCn)
                .map((def) => ({
                  pos: def.pos?.trim() || "",
                  meaningCn: def.meaningCn?.trim() || "",
                })),
            })),
        };

        // 如果发音为空，设置为 null
        if (
          !requestData.pronunciation.uk &&
          !requestData.pronunciation.us &&
          !requestData.pronunciation.ko &&
          !requestData.pronunciation.jp &&
          !requestData.pronunciation.romaji
        ) {
          requestData.pronunciation = null;
        }

        console.log("更新单词请求数据:", requestData);

        const response = await word.updateWord(requestData);

        if (response.code === 200) {
          this.$message.success("更新成功");
          this.closeEditDialog();
          // 重新加载单词列表和当前单词
          await this.loadWords();
          // 重新选中当前单词
          if (this.selectedWordId) {
            const updatedWord = this.wordList.find(
              (w) =>
                w.id === this.selectedWordId || w.wordId === this.selectedWordId
            );
            if (updatedWord) {
              this.selectWord(updatedWord);
            }
          }
        } else {
          this.$message.error(response.msg || "更新失败");
        }
      } catch (error) {
        console.error("更新单词失败:", error);
        this.$message.error("更新失败，请重试");
      } finally {
        this.updating = false;
      }
    },

    // 标签相关方法
    addTag() {
      this.editForm.tags.push("");
    },

    removeTag(index) {
      this.editForm.tags.splice(index, 1);
    },

    // 定义相关方法
    addDefinition() {
      this.editForm.definitions.push({
        pos: "",
        meaningCn: "",
      });
    },

    removeDefinition(index) {
      this.editForm.definitions.splice(index, 1);
    },

    // 例句相关方法
    addExample() {
      this.editForm.examples.push({
        id: null,
        sentence: "",
        translation: "",
        wordPositions: [],
      });
    },

    removeExample(index) {
      this.editForm.examples.splice(index, 1);
    },

    addWordPosition(exampleIndex) {
      if (!this.editForm.examples[exampleIndex].wordPositions) {
        this.editForm.examples[exampleIndex].wordPositions = [];
      }
      this.editForm.examples[exampleIndex].wordPositions.push({
        start: 0,
        end: 0,
      });
    },

    removeWordPosition(exampleIndex, positionIndex) {
      this.editForm.examples[exampleIndex].wordPositions.splice(
        positionIndex,
        1
      );
    },

    // 特征相关方法
    addFeature() {
      this.editForm.features.push({
        type: "",
        items: [],
      });
    },

    removeFeature(index) {
      this.editForm.features.splice(index, 1);
    },

    addFeatureItem(featureIndex) {
      if (!this.editForm.features[featureIndex].items) {
        this.editForm.features[featureIndex].items = [];
      }
      this.editForm.features[featureIndex].items.push({
        content: "",
        meaningCn: "",
      });
    },

    removeFeatureItem(featureIndex, itemIndex) {
      this.editForm.features[featureIndex].items.splice(itemIndex, 1);
    },

    // 相似单词相关方法
    addSimilarWord() {
      this.editForm.similarWords.push({
        wordId: null,
        content: "",
        definitions: [],
      });
    },

    removeSimilarWord(index) {
      this.editForm.similarWords.splice(index, 1);
    },

    addSimilarDefinition(similarWordIndex) {
      if (!this.editForm.similarWords[similarWordIndex].definitions) {
        this.editForm.similarWords[similarWordIndex].definitions = [];
      }
      this.editForm.similarWords[similarWordIndex].definitions.push({
        pos: "",
        meaningCn: "",
      });
    },

    removeSimilarDefinition(similarWordIndex, definitionIndex) {
      this.editForm.similarWords[similarWordIndex].definitions.splice(
        definitionIndex,
        1
      );
    },
  },
};
</script>

<style scoped>
.word-table-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #faf9de;
  padding: 20px;
  overflow: hidden;
}

/* 筛选栏样式 */
.filter-bar {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.filter-form {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.form-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-item label {
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
}

.form-input,
.form-select {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.search-input-group {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

.search-input {
  width: 200px;
  min-width: 150px;
}

.btn-clear {
  padding: 4px 8px;
  background: #f5f5f5;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #666;
  font-size: 12px;
  line-height: 1;
  min-width: auto;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-clear:hover {
  background: #e8e8e8;
  color: #333;
  border-color: #c0c4cc;
}

.btn {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #2c3e50;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #ffed4a 0%, #ffd700 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(255, 215, 0, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-pagination {
  background: white;
  border: 1px solid #dcdfe6;
  color: #2c3e50;
  padding: 6px 16px;
}

.btn-pagination:hover:not(:disabled) {
  background: #ffd700;
  border-color: #ffd700;
  color: #2c3e50;
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 600;
  color: #2c3e50;
  min-width: 60px;
  text-align: center;
}

/* 左右分栏布局 */
.word-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  gap: 20px;
}

/* 左侧：单词列表 */
.word-list-panel {
  width: 400px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  border-radius: 12px 12px 0 0;
}

.panel-header span:first-child {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.word-count {
  font-size: 14px;
  color: #666;
  background: white;
  padding: 4px 12px;
  border-radius: 12px;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.word-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.word-item {
  background: #f8f9fa;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.word-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #ffd700;
  transform: translateX(4px);
}

.word-item.active {
  border-color: #ffd700;
  background: #fffef0;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

.word-content-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.word-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.word-text {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  flex: 1;
}

.word-id {
  font-size: 12px;
  color: #666;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  white-space: nowrap;
}

.word-pronunciation {
  font-size: 12px;
  color: #666;
  display: flex;
  gap: 8px;
}

.word-definitions-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.definition-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
}

.more-tag {
  background: #f5f5f5;
  color: #666;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
}

.word-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.difficulty-badge {
  background: #4caf50;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.tag-item {
  background: #fff3e0;
  color: #e65100;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
}

.tag-more {
  color: #666;
  font-size: 10px;
}

/* 右侧：单词详情 */
.word-detail-panel {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.word-detail-content {
  padding: 24px;
  overflow-y: auto;
  height: 100%;
}

.word-title-section {
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 2px solid #ffd700;
}

.word-title {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.word-text-large {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
}

.word-pronunciation-large {
  font-size: 18px;
  color: #666;
  display: flex;
  gap: 12px;
}

.word-meta-large {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.difficulty-badge-large {
  background: #4caf50;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
}

.tags-large {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tag-badge-large {
  background: #fff3e0;
  color: #e65100;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
}

.empty-detail {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-detail p {
  font-size: 16px;
  color: #999;
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  padding-bottom: 8px;
  border-bottom: 2px solid #ffd700;
}

.definitions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.definition-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
}

.pos {
  font-weight: 600;
  color: #1976d2;
  min-width: 50px;
}

.meaning {
  color: #2c3e50;
  flex: 1;
}

.examples-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.example-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #ffd700;
}

.example-sentence {
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 6px;
  font-style: italic;
}

.example-translation {
  font-size: 13px;
  color: #666;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.feature-type {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 14px;
}

.feature-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.feature-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.feature-content {
  font-weight: 600;
  color: #1976d2;
  min-width: 100px;
}

.feature-meaning {
  color: #666;
  font-size: 13px;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-badge {
  background: #fff3e0;
  color: #e65100;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
}

/* 加载和空状态 */
.loading-state,
.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.word-list.loading {
  opacity: 0.6;
}

/* 滚动条样式 */
.panel-body::-webkit-scrollbar,
.word-detail-content::-webkit-scrollbar {
  width: 8px;
}

.panel-body::-webkit-scrollbar-track,
.word-detail-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.panel-body::-webkit-scrollbar-thumb,
.word-detail-content::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 10px;
}

.panel-body::-webkit-scrollbar-thumb:hover,
.word-detail-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}

/* 自定义书本选择器样式 */
.custom-book-select {
  position: relative;
  width: 300px;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 32px;
}

.select-trigger:hover {
  border-color: #c0c4cc;
}

.select-trigger.active {
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.select-value {
  flex: 1;
  font-size: 14px;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-arrow {
  font-size: 12px;
  color: #c0c4cc;
  transition: transform 0.3s ease;
  margin-left: 8px;
  font-style: normal;
}

.select-arrow.arrow-up {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

.dropdown-container {
  display: flex;
  width: 100%;
  height: 300px;
  border-radius: 6px;
  overflow: hidden;
}

/* 左侧分类栏 */
.book-categories {
  width: 140px;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  overflow-y: auto;
}

.category-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.category-item:hover {
  background-color: #fff9c4;
  color: #2c3e50;
}

.category-item.active {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #2c3e50;
  font-weight: 600;
}

/* 右侧选项栏 */
.book-options {
  flex: 1;
  overflow-y: auto;
  background: white;
}

.book-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
}

.book-option:hover {
  background-color: #fff9c4;
  color: #2c3e50;
}

.book-option.selected {
  background-color: #ffd700;
  color: #000;
  font-weight: 600;
}

.no-books {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* 滚动条样式 */
.book-categories::-webkit-scrollbar,
.book-options::-webkit-scrollbar {
  width: 6px;
}

.book-categories::-webkit-scrollbar-track,
.book-options::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.book-categories::-webkit-scrollbar-thumb,
.book-options::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 3px;
}

.book-categories::-webkit-scrollbar-thumb:hover,
.book-options::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}

/* 编辑按钮样式 */
.btn-edit-word {
  margin-left: 12px;
  padding: 6px 16px;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-edit-word:hover {
  background: linear-gradient(135deg, #45a049 0%, #4caf50 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
}

/* 编辑对话框样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.edit-word-dialog {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  font-weight: bold;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}

.dialog-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.dialog-footer {
  padding: 16px 24px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #fafafa;
}

.cancel-btn,
.confirm-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn {
  background: #f5f5f5;
  color: #666;
}

.cancel-btn:hover {
  background: #e8e8e8;
  color: #333;
}

.confirm-btn {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #2c3e50;
}

.confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ffed4a 0%, #ffd700 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(255, 215, 0, 0.3);
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group .required {
  color: #f56c6c;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

/* 发音组 */
.pronunciation-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.pronunciation-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pronunciation-item label {
  font-size: 12px;
  color: #666;
  margin-bottom: 0;
}

/* 标签输入组 */
.tags-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tag-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.tag-item input {
  flex: 1;
}

.btn-remove-tag,
.btn-remove,
.btn-remove-small {
  background: #f56c6c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  min-width: 32px;
}

.btn-remove-small {
  padding: 4px 8px;
  font-size: 12px;
  min-width: 24px;
}

.btn-remove-tag:hover,
.btn-remove:hover,
.btn-remove-small:hover {
  background: #f78989;
}

.btn-add-tag,
.btn-add,
.btn-add-small {
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  width: fit-content;
}

.btn-add-small {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-add-tag:hover,
.btn-add:hover,
.btn-add-small:hover {
  background: #45a049;
}

/* 定义编辑组 */
.definitions-edit-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.definition-edit-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.definition-edit-item .pos-input {
  width: 80px !important;
  flex-shrink: 0;
}

.meaning-input {
  flex: 1;
  min-width: 200px;
}

/* 例句编辑组 */
.examples-edit-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.example-edit-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.example-edit-header {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.sentence-input {
  flex: 1;
}

.translation-input {
  width: 100%;
  margin-bottom: 8px;
}

.word-positions-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #ddd;
}

.position-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.position-input {
  width: 100px;
}

.position-item span {
  color: #666;
  font-size: 14px;
}

/* 特征编辑组 */
.features-edit-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-edit-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.feature-edit-header {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.feature-type-input {
  flex: 1;
}

.feature-items-edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 12px;
  border-left: 2px solid #ffd700;
}

.feature-item-edit {
  display: flex;
  gap: 8px;
  align-items: center;
}

.feature-content-input {
  flex: 1;
}

.feature-meaning-input {
  flex: 1;
}

/* 相似单词显示样式 */
.similar-words-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.similar-word-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #17a2b8;
}

.similar-word-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.similar-word-content {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.similar-word-id {
  font-size: 12px;
  color: #666;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 4px;
}

.similar-word-definitions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.similar-definition {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.similar-pos {
  font-weight: 600;
  color: #17a2b8;
  min-width: 40px;
  font-size: 13px;
}

.similar-meaning {
  color: #2c3e50;
  flex: 1;
  font-size: 13px;
}

/* 相似单词编辑样式 */
.similar-words-edit-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.similar-word-edit-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.similar-word-edit-header {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.similar-word-id-input {
  width: 100px !important;
  flex-shrink: 0;
}

.similar-word-content-input {
  flex: 1;
}

.similar-definitions-edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 12px;
  border-left: 2px solid #17a2b8;
}

.similar-definition-edit {
  display: flex;
  gap: 8px;
  align-items: center;
}

.similar-pos-input {
  width: 80px !important;
  flex-shrink: 0;
}

.similar-meaning-input {
  flex: 1;
  min-width: 200px;
}
</style>
